import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Sparkles } from "lucide-react";

const inr = (n) => `₹${Number(Math.round(n) || 0).toLocaleString("en-IN")}`;

/**
 * RechargeSuccessSplash
 * ─────────────────────
 * Full-screen gold-pulse celebration that replaces the plain success
 * toast after a Razorpay top-up completes. Shows the new balance in
 * huge serif type over a radial gold wash. Auto-dismisses after ~3.5s
 * or when the user clicks anywhere.
 */
export function RechargeSuccessSplash({
  open, onClose, amount = 0, balanceAfter = 0, tier,
}) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="recharge-success-splash"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] grid place-items-center cursor-pointer overflow-hidden"
          style={{
            background:
              `radial-gradient(1200px 700px at 50% 50%, ${tier.glow}, rgba(5,5,5,0.98))`,
            backdropFilter: "blur(24px)",
          }}
        >
          {/* expanding gold pulse rings */}
          {[0, 0.3, 0.6].map((delay, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full pointer-events-none"
              style={{
                width: 260, height: 260,
                border: `1px solid ${tier.accent}`,
                boxShadow: `0 0 60px ${tier.glow}`,
              }}
              initial={{ scale: 0.4, opacity: 0.8 }}
              animate={{ scale: 5, opacity: 0 }}
              transition={{ duration: 2.2, delay, repeat: Infinity, ease: "easeOut" }}
            />
          ))}

          <motion.div
            initial={{ scale: 0.7, opacity: 0, y: 40 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
            className="relative text-center px-8"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1, rotate: [0, 12, 0] }}
              transition={{ type: "spring", stiffness: 260, damping: 14, delay: 0.15 }}
              className="w-24 h-24 mx-auto rounded-full grid place-items-center"
              style={{
                background: `linear-gradient(135deg, ${tier.primary}, ${tier.accent})`,
                boxShadow: `0 0 80px ${tier.glow}, 0 0 140px ${tier.glow}`,
              }}
            >
              <CheckCircle2 size={44} className="text-black" strokeWidth={2.4} />
            </motion.div>

            <div
              className="font-grotesk text-[10px] uppercase tracking-[0.6em] mt-8"
              style={{ color: tier.accent }}
            >
              <Sparkles size={12} className="inline -mt-0.5 mr-2" />
              Balance Credited
            </div>

            <div
              className="font-serif text-6xl md:text-7xl tracking-tight mt-4 text-[#F5F5F0]"
              style={{ textShadow: `0 0 60px ${tier.glow}` }}
              data-testid="recharge-success-amount"
            >
              +{inr(amount)}
            </div>

            <div
              className="font-grotesk text-[10px] uppercase tracking-[0.32em] mt-6"
              style={{ color: tier.accentSoft }}
            >
              New wallet balance
            </div>
            <div
              className="font-serif text-2xl md:text-3xl mt-1"
              style={{ color: tier.primary }}
              data-testid="recharge-success-balance"
            >
              {inr(balanceAfter)}
            </div>

            <div className="text-[10px] font-body text-[#8A8A82] italic mt-8">
              Tap anywhere to continue
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
